"use client";

import { useState, useEffect, useRef, type FormEvent, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";

/* ───── DATA ───── */
const BROAD_ROLES = [
  { id: "lender", label: "Lender" },
  { id: "investor", label: "Investor" },
  { id: "broker", label: "Broker / Agent" },
  { id: "service", label: "Service Provider (Title, Legal, Software, etc.)" },
  { id: "other", label: "None of the above" },
] as const;

const SUB_ROLES: Record<string, { headline: string; options?: string[]; freeText?: boolean }> = {
  lender: {
    headline: "What type of lender?",
    options: [
      "Private / Hard Money Lender",
      "Bank or Credit Union",
      "Debt Broker",
      "Debt Buyer",
      "Distressed Debt Buyer",
      "Other Lender",
    ],
  },
  investor: {
    headline: "What type of investor?",
    options: [
      "Residential Investor",
      "Multifamily Investor",
      "Commercial Investor",
      "Land Investor",
      "Industrial Investor",
      "Debt Buyer",
      "Distressed Debt Buyer",
    ],
  },
  broker: {
    headline: "What type of broker or agent?",
    options: [
      "Commercial Investment Sales Broker",
      "Commercial Debt & Equity Broker",
      "Residential Agent",
      "Residential Mortgage Broker",
      "Other",
    ],
  },
  service: {
    headline: "What type of service?",
    options: [
      "Title / Settlement",
      "Attorney / Legal",
      "Insurance",
      "Accounting / Tax",
      "Software / PropTech",
      "Property Management",
      "Appraiser",
      "Contractor",
      "Consultant",
      "Other",
    ],
  },
  other: {
    headline: "What do you do?",
    freeText: true,
  },
};

const INTENT: Record<string, { headline: string; options: string[] }> = {
  lender: {
    headline: "What brought you here today?",
    options: [
      "Researching a potential borrower",
      "Prospecting for new borrowers",
      "Running due diligence on a deal",
      "Monitoring my existing portfolio",
      "Just exploring the platform",
    ],
  },
  investor: {
    headline: "What brought you here today?",
    options: [
      "Checking my own profile",
      "Researching a partner or competitor",
      "Looking for financing options",
      "Evaluating a market or deal",
      "Just exploring the platform",
    ],
  },
  broker: {
    headline: "What brought you here today?",
    options: [
      "Researching on behalf of a client",
      "Prospecting for new business",
      "Running due diligence on a deal",
      "Evaluating a market",
      "Just exploring the platform",
    ],
  },
  service: {
    headline: "What brought you here today?",
    options: [
      "Researching a client",
      "Prospecting for new business",
      "Running due diligence",
      "Just exploring the platform",
    ],
  },
  other: {
    headline: "What brought you here today?",
    options: [
      "Researching someone specific",
      "Prospecting for new business",
      "General curiosity",
      "Just exploring the platform",
    ],
  },
};

/* ───── STEPS ───── */
type Step = "email" | "role" | "subrole" | "intent" | "done";
const STEPS: Step[] = ["email", "role", "subrole", "intent", "done"];

/* ───── ANIMATION WRAPPER ───── */
function Fade({ children, show, delay = 0 }: { children: ReactNode; show: boolean; delay?: number }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [show, delay]);

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
      )}
    >
      {children}
    </div>
  );
}

/* ───── OPTION BUTTON ───── */
function OptionButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-lg border px-4 py-3 text-left text-sm font-medium transition-all",
        active
          ? "border-blue-400 bg-blue-50 text-zinc-900"
          : "border-zinc-200 bg-white text-zinc-800 hover:bg-zinc-50"
      )}
    >
      {children}
    </button>
  );
}

/* ───── POSTHOG SURVEY CONFIG ───── */
const POSTHOG_SURVEY_ID = "019c68d6-f82e-0000-05b3-8b390662b617";
const POSTHOG_SURVEY_NAME = "Investor Profile Unlock Survey";

const Q_EMAIL_ID = "c4ebfbe9-2a1a-4eed-8a67-4f479d7f61b1";
const Q_ROLE_ID = "526be3e3-e0ed-488f-af24-dca4f61d1755";
const Q_SUBROLE_ID = "f2744654-d711-4ae3-a989-2c19336a710e";
const Q_INTENT_ID = "3bea0a25-2db4-4ae9-b4bb-269bb3482fb5";

const SURVEY_QUESTIONS = [
  { id: Q_EMAIL_ID, question: "Enter your email to continue" },
  { id: Q_ROLE_ID, question: "What best describes you?" },
  { id: Q_SUBROLE_ID, question: "What type specifically?" },
  { id: Q_INTENT_ID, question: "What brought you here today?" },
];

/* ───── MAIN COMPONENT ───── */
interface SurveyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SurveyModal({ open, onOpenChange }: SurveyModalProps) {
  const submissionId = useRef("");
  const [step, setStep] = useState<Step>("email");
  const [transitioning, setTransitioning] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [broadRole, setBroadRole] = useState<string | null>(null);
  const [subRole, setSubRole] = useState<string | null>(null);
  const [intent, setIntent] = useState<string | null>(null);
  const [freeText, setFreeText] = useState("");

  const go = (next: Step) => {
    setTransitioning(true);
    setTimeout(() => {
      setStep(next);
      setTransitioning(false);
    }, 200);
  };

  const reset = () => {
    // Partial answers belong to one billable submission per modal opening.
    submissionId.current = crypto.randomUUID();
    setStep("email");
    setEmail("");
    setEmailError("");
    setBroadRole(null);
    setSubRole(null);
    setIntent(null);
    setFreeText("");
  };

  // Reset when modal opens, fire "survey shown"
  // Track dismiss on close when not completed
  useEffect(() => {
    if (open) {
      reset();
      posthog.capture("survey shown", {
        $survey_id: POSTHOG_SURVEY_ID,
        $survey_name: POSTHOG_SURVEY_NAME,
      });
    }
  }, [open]);

  // Fire "survey dismissed" when modal closes before completion
  const handleOpenChange = (next: boolean) => {
    if (!next && step !== "done") {
      posthog.capture("survey dismissed", {
        $survey_id: POSTHOG_SURVEY_ID,
        $survey_name: POSTHOG_SURVEY_NAME,
        dismissed_at_step: step,
        partial_email: email || undefined,
        partial_role: broadRole
          ? BROAD_ROLES.find((r) => r.id === broadRole)?.label
          : undefined,
        partial_sub_role: subRole || undefined,
      });
    }
    onOpenChange(next);
  };

  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError("");

    // Identify the user and send partial survey response for Q1
    posthog.identify(email, { email });
    posthog.capture("survey sent", {
      $survey_id: POSTHOG_SURVEY_ID,
      $survey_name: POSTHOG_SURVEY_NAME,
      [`$survey_response_${Q_EMAIL_ID}`]: email,
      $survey_questions: SURVEY_QUESTIONS,
      survey_step: "email",
      survey_partial: true,
      $survey_submission_id: submissionId.current,
      $survey_completed: false,
    });

    go("role");
  };

  const pickRole = (id: string) => {
    setBroadRole(id);

    // Send partial survey response for Q2
    const roleLabel = BROAD_ROLES.find((r) => r.id === id)?.label || id;
    posthog.capture("survey sent", {
      $survey_id: POSTHOG_SURVEY_ID,
      $survey_name: POSTHOG_SURVEY_NAME,
      [`$survey_response_${Q_EMAIL_ID}`]: email,
      [`$survey_response_${Q_ROLE_ID}`]: roleLabel,
      $survey_questions: SURVEY_QUESTIONS,
      survey_step: "role",
      survey_partial: true,
      $survey_submission_id: submissionId.current,
      $survey_completed: false,
    });
    posthog.people.set({ role: roleLabel });

    setTimeout(() => go("subrole"), 300);
  };

  const pickSubRole = (val: string) => {
    setSubRole(val);
    setTimeout(() => go("intent"), 300);
  };

  const submitFreeText = (e: FormEvent) => {
    e.preventDefault();
    if (!freeText.trim()) return;
    setSubRole(freeText);
    go("intent");
  };

  const pickIntent = (val: string) => {
    setIntent(val);

    // Send final complete survey response with all answers
    const roleLabel = BROAD_ROLES.find((r) => r.id === broadRole)?.label || broadRole;
    posthog.capture("survey sent", {
      $survey_id: POSTHOG_SURVEY_ID,
      $survey_name: POSTHOG_SURVEY_NAME,
      [`$survey_response_${Q_EMAIL_ID}`]: email,
      [`$survey_response_${Q_ROLE_ID}`]: roleLabel,
      [`$survey_response_${Q_SUBROLE_ID}`]: subRole,
      [`$survey_response_${Q_INTENT_ID}`]: val,
      $survey_questions: SURVEY_QUESTIONS,
      survey_step: "complete",
      survey_partial: false,
      $survey_submission_id: submissionId.current,
      $survey_completed: true,
    });
    posthog.people.set({
      sub_role: subRole,
      intent: val,
    });

    setTimeout(() => go("done"), 300);
  };

  const stepIndex = STEPS.indexOf(step);
  const totalSteps = 4;
  const progressPct = step === "done" ? 100 : (stepIndex / totalSteps) * 100;

  const subRoleData = broadRole ? SUB_ROLES[broadRole] : null;
  const intentData = broadRole ? INTENT[broadRole] : null;
  const isService = broadRole === "service";

  const DEMO_QUALIFYING_SUB_ROLES = [
    "Debt Buyer",
    "Distressed Debt Buyer",
    "Commercial Debt & Equity Broker",
  ];
  const showBookDemo =
    broadRole === "lender" ||
    (subRole !== null && DEMO_QUALIFYING_SUB_ROLES.includes(subRole));

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-[460px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg outline-none">

          {/* Progress bar */}
          <div className="h-[3px] w-full bg-zinc-100">
            <div
              className="h-full bg-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Close */}
          <Dialog.Close className="absolute right-4 top-3.5 z-10 rounded-md p-1 text-zinc-400 transition-colors hover:text-zinc-600">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Dialog.Close>

          {/* Content */}
          <div
            className={cn(
              "min-h-[320px] px-8 pb-8 pt-7 transition-all duration-200",
              transitioning ? "scale-[0.98] opacity-0" : "scale-100 opacity-100"
            )}
          >
            {/* ── EMAIL ── */}
            {step === "email" && (
              <Fade show={!transitioning}>
                <p className="mb-4 text-xs font-medium uppercase tracking-widest text-zinc-400">
                  Step 1 of 4
                </p>
                <h3 className="mb-1.5 text-xl font-medium text-zinc-900">
                  Enter your email to continue
                </h3>
                <p className="mb-5 text-sm text-zinc-600">
                  Get access to full investor profiles and mortgage data.
                </p>
                <form onSubmit={submitEmail}>
                  <input
                    type="email"
                    value={email}
                    autoFocus
                    onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                    placeholder="you@company.com"
                    className={cn(
                      "w-full rounded-lg border-2 bg-zinc-50 px-4 py-3 text-sm outline-none transition-all placeholder:text-zinc-400",
                      emailError
                        ? "border-red-400"
                        : "border-zinc-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                    )}
                  />
                  {emailError && (
                    <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
                  )}
                  <button
                    type="submit"
                    className="mt-3.5 w-full rounded-lg bg-zinc-900 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
                  >
                    Continue
                  </button>
                </form>
              </Fade>
            )}

            {/* ── BROAD ROLE ── */}
            {step === "role" && (
              <Fade show={!transitioning}>
                <p className="mb-4 text-xs font-medium uppercase tracking-widest text-zinc-400">
                  Step 2 of 4
                </p>
                <h3 className="mb-1.5 text-xl font-medium text-zinc-900">
                  What best describes you?
                </h3>
                <p className="mb-5 text-sm text-zinc-600">
                  Helps us tailor your experience.
                </p>
                <div className="flex flex-col gap-2">
                  {BROAD_ROLES.map((r, i) => (
                    <Fade show={!transitioning} delay={i * 40} key={r.id}>
                      <OptionButton
                        active={broadRole === r.id}
                        onClick={() => pickRole(r.id)}
                      >
                        {r.label}
                      </OptionButton>
                    </Fade>
                  ))}
                </div>
              </Fade>
            )}

            {/* ── SUB ROLE ── */}
            {step === "subrole" && subRoleData && (
              <Fade show={!transitioning}>
                <p className="mb-4 text-xs font-medium uppercase tracking-widest text-zinc-400">
                  Step 3 of 4
                </p>
                <h3 className="mb-1.5 text-xl font-medium text-zinc-900">
                  {subRoleData.headline}
                </h3>
                <p className="mb-5 text-sm text-zinc-600">One more click.</p>

                {subRoleData.freeText ? (
                  <form onSubmit={submitFreeText}>
                    <input
                      type="text"
                      value={freeText}
                      autoFocus
                      onChange={(e) => setFreeText(e.target.value)}
                      placeholder="e.g. Journalist, Student, Government..."
                      className="w-full rounded-lg border-2 border-zinc-200 bg-zinc-50 px-4 py-3 text-sm outline-none transition-all placeholder:text-zinc-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                    />
                    <button
                      type="submit"
                      className="mt-3.5 w-full rounded-lg bg-zinc-900 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-zinc-800"
                    >
                      Continue
                    </button>
                  </form>
                ) : (
                  <div
                    className={cn(
                      "gap-2",
                      isService ? "grid grid-cols-2" : "flex flex-col"
                    )}
                  >
                    {subRoleData.options?.map((opt, i) => (
                      <Fade show={!transitioning} delay={i * 35} key={opt}>
                        <OptionButton
                          active={subRole === opt}
                          onClick={() => pickSubRole(opt)}
                        >
                          {opt}
                        </OptionButton>
                      </Fade>
                    ))}
                  </div>
                )}
              </Fade>
            )}

            {/* ── INTENT ── */}
            {step === "intent" && intentData && (
              <Fade show={!transitioning}>
                <p className="mb-4 text-xs font-medium uppercase tracking-widest text-zinc-400">
                  Step 4 of 4
                </p>
                <h3 className="mb-1.5 text-xl font-medium text-zinc-900">
                  {intentData.headline}
                </h3>
                <p className="mb-5 text-sm text-zinc-600">
                  Last one - helps us build the right features.
                </p>
                <div className="flex flex-col gap-2">
                  {intentData.options.map((opt, i) => (
                    <Fade show={!transitioning} delay={i * 40} key={opt}>
                      <OptionButton
                        active={intent === opt}
                        onClick={() => pickIntent(opt)}
                      >
                        {opt}
                      </OptionButton>
                    </Fade>
                  ))}
                </div>
              </Fade>
            )}

            {/* ── DONE ── */}
            {step === "done" && (
              <Fade show={!transitioning}>
                <div className="flex flex-col items-center pt-4 text-center">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-xl text-emerald-600">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-medium text-zinc-900">
                    {showBookDemo ? "We should talk" : "You\u2019re on the list"}
                  </h3>
                  <p className="mb-6 max-w-[300px] text-sm text-zinc-600">
                    {showBookDemo
                      ? "Only Elementix is built for this use case."
                      : "We\u2019ll notify you when full profiles go live."}
                  </p>

                  {showBookDemo && (
                    <a
                      href="/contact"
                      onClick={() => {
                        posthog.capture("survey_book_demo_clicked", {
                          role: BROAD_ROLES.find((r) => r.id === broadRole)?.label,
                          sub_role: subRole,
                        });
                      }}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                    >
                      Book a Demo
                      <span className="iconify solar--arrow-right-linear text-sm"></span>
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={() => handleOpenChange(false)}
                    className={cn(
                      "rounded-lg px-8 py-3 text-sm font-medium transition-colors",
                      showBookDemo
                        ? "mt-3 text-zinc-600 hover:text-zinc-900"
                        : "mt-5 bg-zinc-900 text-white hover:bg-zinc-800"
                    )}
                  >
                    Close
                  </button>
                </div>
              </Fade>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
